/**
 * The Layers registry — every layer used across every application in the
 * portal, in one list, with its dependants: which apps actually use it.
 *
 * Built entirely from the SUMMARY documents the portal listing already
 * holds (see `moduleService`/`services.summary_config` on the server) —
 * loading every module's FULL document just to read layer metadata would
 * pull every uploaded feature along with it, the exact cost the summary
 * exists to avoid. A summary keeps enough of each layer (name, service
 * reference, `localData`/`localRaster` presence) to classify and label it;
 * only the "Layer information" detail fetch below makes its own request,
 * one layer at a time, when someone actually opens it.
 *
 * "The same layer" across two applications means the same SERVICE layer —
 * matched by its ArcGIS service URL + sub-layer id, or its WMS URL + layer
 * name — not by name alone, which two unrelated services could share. A
 * file-backed layer can never be the same one twice: each import is its
 * own copy of data with nothing to point back to, so it always reports
 * exactly the one application it was added to.
 */

import { useMemo, useRef, useState } from 'react';
import { ArrowLeft, Building2, Layers as LayersIcon, MoreVertical, Search, Share2 } from 'lucide-react';
import type { AppModule, Layer, User } from '../../types';
import { detectLayerSource, getLayerCapabilities, getLayerDisplayName } from '../../layers/capabilities';
import { getLayerAdapter } from '../../layers/adapters';
import { useAuth } from '../../auth/AuthContext';
import { layerLibraryService, type OrgSharedLayerEntry } from '../../services/layerLibraryService';
import LayerOptionsMenu, { type LayerMenuItem } from '../layers/LayerOptionsMenu';
import LayerShareDialog from '../layers/LayerShareDialog';

interface RegistryEntry {
  key: string;
  displayName: string;
  sourceLabel: string;
  /** One real layer + the module it came from — enough for `fetchDetails`,
   *  which only ever needs to ask the service once, not once per app. */
  sampleLayer: Layer;
  sampleModule: AppModule;
  usedIn: { moduleId: string; moduleTitle: string }[];
}

function buildRegistry(modules: AppModule[]): RegistryEntry[] {
  const map = new Map<string, RegistryEntry>();

  for (const module of modules) {
    for (const layer of module.layers ?? []) {
      if (layer.isGroup) continue; // a TOC folder, not a drawn layer

      const sourceKind = detectLayerSource(layer, module);
      const key =
        sourceKind === 'arcgis-feature' || sourceKind === 'arcgis-map'
          ? `arcgis|${module.arcgisUrl ?? ''}|${layer.arcgisLayerId}`
          : sourceKind === 'wms'
            ? `wms|${module.geoServerUrl ?? ''}|${layer.name}`
            : // local / local-raster: file-backed, so this exact layer can
              // only ever belong to this one module.
              `${sourceKind}|${module.id}|${layer.id}`;

      const usedInEntry = { moduleId: module.id, moduleTitle: module.title || module.name };
      const existing = map.get(key);
      if (existing) {
        if (!existing.usedIn.some((entry) => entry.moduleId === module.id)) {
          existing.usedIn.push(usedInEntry);
        }
        continue;
      }

      map.set(key, {
        key,
        displayName: getLayerDisplayName(layer),
        sourceLabel: getLayerCapabilities(layer, module).sourceLabel,
        sampleLayer: layer,
        sampleModule: module,
        usedIn: [usedInEntry],
      });
    }
  }

  return Array.from(map.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export default function LayersRegistryTab({
  modules,
  onOpenApp,
  ownerName,
  users,
  orgSharedLayers,
}: {
  modules: AppModule[];
  /** Opens one of "used in"'s applications in the Builder. */
  onOpenApp: (id: string) => void;
  /** Who owns this layer (its sample module's owner) — shown next to the
   *  source label when provided. Omitted by My Space (every row is the
   *  viewer's own); passed by Organization. */
  ownerName?: (entry: RegistryEntry) => string;
  /** Every user in the portal, for resolving a layer's share grant (user
   *  ids) into names in the detail view — already loaded once by the
   *  caller, the same list `ShareModuleDialog` loads, so this makes no
   *  second fetch of its own. */
  users?: User[];
  /**
   * Layers someone has shared with the WHOLE organization — fetched
   * portal-wide by the caller (Organization scope only; My Space omits
   * this), since the owning module may not otherwise be visible here at
   * all. Shown as its own section, not merged into the per-module registry
   * above: there is no full `AppModule` for these to hand the registry's
   * adapter-based "Layer information" fetch, so they get their own,
   * simpler detail view instead.
   */
  orgSharedLayers?: OrgSharedLayerEntry[];
}) {
  const { currentUser } = useAuth();
  const registry = useMemo(() => buildRegistry(modules), [modules]);
  const [query, setQuery] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedOrgLayer, setSelectedOrgLayer] = useState<OrgSharedLayerEntry | null>(null);
  const [rows, setRows] = useState<{ label: string; value: string }[] | null>(null);
  /** The layer's current share grant, resolved to names — null while
   *  loading/not applicable, [] once loaded with nothing shared. */
  const [sharedWithNames, setSharedWithNames] = useState<string[] | null>(null);
  /** Whether the layer is shared with the whole organization — null while
   *  loading/not applicable. Read-only here; set from LayerShareDialog. */
  const [orgShared, setOrgShared] = useState<boolean | null>(null);
  /** Which row's 3-dot menu is open, and the button it hangs off — same
   *  pattern the in-map TOC uses for its own per-layer menu. */
  const [menuEntryKey, setMenuEntryKey] = useState<string | null>(null);
  const menuAnchorRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  /** The entry whose "Share this layer" dialog is open. */
  const [sharingEntry, setSharingEntry] = useState<RegistryEntry | null>(null);

  /** Only the layer's OWNER — or an admin, who can already manage anything —
   *  may share it. Mirrors the same rule the in-map TOC's own "Share this
   *  layer" item enforces, and that the server enforces regardless. */
  const canShare = (entry: RegistryEntry) =>
    Boolean(currentUser) &&
    (currentUser?.role === 'admin' || entry.sampleModule.ownerId === currentUser?.id);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return registry;
    return registry.filter((entry) => entry.displayName.toLowerCase().includes(term));
  }, [registry, query]);

  const selected = registry.find((entry) => entry.key === selectedKey) ?? null;

  const open = async (entry: RegistryEntry) => {
    setSelectedKey(entry.key);
    setRows(null);
    setSharedWithNames(null);
    setOrgShared(null);
    try {
      // The SAME per-service call the in-map "Layer information" panel
      // makes — one request, made now, not one per app this layer is used in.
      const details = await getLayerAdapter(entry.sampleLayer, entry.sampleModule).fetchDetails(
        entry.sampleLayer,
        entry.sampleModule,
      );
      setRows(details.rows);
    } catch {
      setRows([{ label: 'Error', value: 'Layer details could not be loaded.' }]);
    }

    // Who this is shared with is owner/admin information, same as whether
    // the Share action itself is offered — see canShare. One request, made
    // only here (the detail view), not per row in the list above.
    if (canShare(entry) && users) {
      try {
        const share = await layerLibraryService.fetchLayerShare(entry.sampleModule.id, entry.sampleLayer.id);
        const byId = new Map(users.map((user) => [user.id, user.name]));
        setSharedWithNames(share.userIds.map((id) => byId.get(id) ?? id));
        setOrgShared(share.sharedWithOrg);
      } catch {
        setSharedWithNames([]);
        setOrgShared(null);
      }
    } else {
      setSharedWithNames([]);
      setOrgShared(null);
    }
  };

  if (registry.length === 0 && (!orgSharedLayers || orgSharedLayers.length === 0)) {
    return (
      <p className="rounded-lg border border-dashed border-slate-300 px-3 py-10 text-center text-sm text-slate-400">
        No layers yet — layers are added from inside an application's builder.
      </p>
    );
  }

  if (selectedOrgLayer) {
    const geometryType =
      selectedOrgLayer.layer.geometryType ??
      (selectedOrgLayer.layer.localData?.features?.[0]?.geometry?.type as string | undefined) ??
      'Unknown';
    const featureCount = selectedOrgLayer.layer.localData?.features?.length;
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelectedOrgLayer(null)}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to all layers
        </button>

        <div>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
            <Building2 className="h-3.5 w-3.5" />
            Shared with the organization
          </p>
          <h3 className="mt-0.5 text-base font-semibold text-slate-900">
            {getLayerDisplayName(selectedOrgLayer.layer)}
          </h3>
          <p className="mt-0.5 text-[11px] text-slate-400">From {selectedOrgLayer.moduleTitle}</p>
        </div>

        <dl className="space-y-2">
          <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Owner</dt>
            <dd className="text-xs text-slate-700">{selectedOrgLayer.ownerName}</dd>
          </div>
          <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Geometry</dt>
            <dd className="text-xs text-slate-700">
              {geometryType}
              {featureCount !== undefined ? ` · ${featureCount.toLocaleString()} features` : ''}
            </dd>
          </div>
          <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Created</dt>
            <dd className="text-xs text-slate-700">{new Date(selectedOrgLayer.createdAt).toLocaleString()}</dd>
          </div>
          <div className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Modified</dt>
            <dd className="text-xs text-slate-700">{new Date(selectedOrgLayer.updatedAt).toLocaleString()}</dd>
          </div>
        </dl>

        <button
          type="button"
          onClick={() => onOpenApp(selectedOrgLayer.moduleId)}
          className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-left text-[12.5px] font-medium text-slate-700 transition hover:border-blue-300 hover:bg-blue-50"
        >
          Open {selectedOrgLayer.moduleTitle}
        </button>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelectedKey(null)}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to all layers
        </button>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-600">Layer information</p>
          <h3 className="mt-0.5 text-base font-semibold text-slate-900">{selected.displayName}</h3>
          <p className="mt-0.5 text-[11px] text-slate-400">
            {selected.sourceLabel}
            {ownerName && ` · Owned by ${ownerName(selected)}`}
          </p>
        </div>

        {rows === null ? (
          <p className="py-6 text-center text-sm text-slate-400">Loading details…</p>
        ) : (
          <dl className="space-y-2">
            {rows.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-[130px_1fr] gap-3 rounded-lg bg-slate-50 px-3 py-2"
              >
                <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {row.label}
                </dt>
                <dd className="break-words text-xs text-slate-700">{row.value}</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="border-t border-slate-100 pt-3">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Used in {selected.usedIn.length} application{selected.usedIn.length === 1 ? '' : 's'}
          </p>
          <ul className="space-y-1">
            {selected.usedIn.map((entry) => (
              <li key={entry.moduleId}>
                <button
                  type="button"
                  onClick={() => onOpenApp(entry.moduleId)}
                  className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-left text-[12.5px] font-medium text-slate-700 transition hover:border-blue-300 hover:bg-blue-50"
                >
                  {entry.moduleTitle}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Only drawn once the share-grant fetch above has actually run —
            canShare(selected) false means this person was never offered a
            reason to know who else can see this layer in the first place. */}
        {canShare(selected) && (
          <div className="border-t border-slate-100 pt-3">
            <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <Share2 className="h-3.5 w-3.5" />
              Shared with
            </p>
            {sharedWithNames === null ? (
              <p className="text-[12px] text-slate-400">Loading…</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {orgShared && (
                  <li className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-[11.5px] font-medium text-indigo-700">
                    Whole organization
                  </li>
                )}
                {sharedWithNames.map((name) => (
                  <li
                    key={name}
                    className="rounded-full bg-teal-50 px-2.5 py-0.5 text-[11.5px] font-medium text-teal-700"
                  >
                    {name}
                  </li>
                ))}
                {!orgShared && sharedWithNames.length === 0 && (
                  <p className="text-[12px] text-slate-400">Not shared with anyone yet.</p>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orgSharedLayers && orgSharedLayers.length > 0 && (
        <div className="space-y-1.5">
          <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
            <Building2 className="h-3.5 w-3.5" />
            Shared with the organization ({orgSharedLayers.length})
          </p>
          <ul className="space-y-1.5">
            {orgSharedLayers.map((entry) => (
              <li key={`${entry.moduleId}:${entry.layer.id}`}>
                <button
                  type="button"
                  onClick={() => setSelectedOrgLayer(entry)}
                  className="flex w-full items-center gap-3 rounded-lg border border-indigo-100 bg-indigo-50/40 px-3 py-2.5 text-left transition hover:border-indigo-300 hover:bg-indigo-50"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white">
                    <LayersIcon className="h-4 w-4 text-indigo-400" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-slate-800">
                      {getLayerDisplayName(entry.layer)}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-slate-400">
                      Owned by {entry.ownerName} · From {entry.moduleTitle}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-[11.5px] text-slate-500">
        Every layer used across every application in the portal. Click one for its full details and
        which applications depend on it.
      </p>

      <label className="relative block">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search layers…"
          className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-3 text-[12.5px] text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
        />
      </label>

      {filtered.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 px-3 py-10 text-center text-sm text-slate-400">
          No layers match “{query}”.
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((entry) => {
            const shareable = canShare(entry);
            const menuItems: LayerMenuItem[] = shareable
              ? [
                  {
                    id: 'share',
                    label: 'Share this layer',
                    icon: Share2,
                    onSelect: () => setSharingEntry(entry),
                  },
                ]
              : [];

            return (
              <li key={entry.key} className="flex items-stretch gap-1.5">
                <button
                  type="button"
                  onClick={() => void open(entry)}
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-left transition hover:border-blue-300 hover:bg-blue-50"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
                    <LayersIcon className="h-4 w-4 text-slate-400" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium text-slate-800">
                      {entry.displayName}
                    </span>
                    <span className="mt-0.5 block text-[11px] text-slate-400">
                      {entry.sourceLabel}
                      {ownerName && ` · Owned by ${ownerName(entry)}`}
                    </span>
                  </span>
                  <span className="flex-shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-500">
                    {entry.usedIn.length} app{entry.usedIn.length === 1 ? '' : 's'}
                  </span>
                </button>

                {shareable && (
                  <div className="relative flex-shrink-0">
                    <button
                      ref={(node) => {
                        menuAnchorRefs.current[entry.key] = node;
                      }}
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setMenuEntryKey((current) => (current === entry.key ? null : entry.key));
                      }}
                      aria-label={`Options for ${entry.displayName}`}
                      aria-expanded={menuEntryKey === entry.key}
                      className="flex h-full items-center justify-center rounded-lg border border-slate-200 bg-white px-2 text-slate-400 transition hover:border-blue-300 hover:bg-blue-50 hover:text-slate-600"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                    {menuEntryKey === entry.key && (
                      <LayerOptionsMenu
                        items={menuItems}
                        anchorRef={{ current: menuAnchorRefs.current[entry.key] ?? null }}
                        onClose={() => setMenuEntryKey(null)}
                        ariaLabel={`Options for ${entry.displayName}`}
                      />
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {sharingEntry && (
        <LayerShareDialog
          moduleId={sharingEntry.sampleModule.id}
          layerId={sharingEntry.sampleLayer.id}
          layerName={sharingEntry.displayName}
          onClose={() => setSharingEntry(null)}
        />
      )}
    </div>
  );
}
